import React, { useState, useMemo, useCallback, lazy, Suspense, useRef } from "react";
import ChannelList from "./components/ChannelList";
import VideoPlayer from "./components/VideoPlayer";
import PlaylistManager from "./components/PlaylistManager";
import UserManagement from "./components/UserManagement";
import UserMenu from "./components/UserMenu";
import Sidebar from "./components/Sidebar";
import ChatWidget from "./components/SportsChat/ChatWidget";
import BroadcastNotificationIcon from "./components/BroadcastNotificationIcon";
import { User } from 'lucide-react';
import { useFavorites } from "./hooks/useFavorites";
import { useM3UPlaylist } from "./hooks/useM3UPlaylist";
import { useAuth } from "./contexts/AuthContext";
import './styles/series.css';

export function AppContent() {
  const { isAuthenticated, isInitialized, user } = useAuth();
  
  // Debounce utility for global search optimization
  const debounce = useCallback((func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }, []);

  // Content type detection function - SIMPLIFIED and memoized
  const detectContentType = useCallback((channel) => {
    const url = channel.url || channel.originalUrl || '';
    
    // Check if URL has a video file extension
    if (/\.(mp4|mkv|avi|mov|wmv|flv|m4v|3gp|webm|ogg|mp3|aac|wav|ts)(\?|$)/i.test(url)) {
      return 'FILM';
    }
    
    // Everything else is LIVE TV
    return 'LIVE';
  }, []);

  const [selectedChannel, setSelectedChannel] = useState(null);
  const [dark, setDark] = useState(true);
  const { channels, parseM3U, defaultGroup, loadingProgress, isLoading, loadManagedPlaylistsContent } = useM3UPlaylist();
  const { favorites, toggleFavorite, isFavorite } = useFavorites();
  const [group, setGroup] = useState("");
  const [hasInitialGroupSet, setHasInitialGroupSet] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPlaylistManager, setShowPlaylistManager] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [playlistsLoading, setPlaylistsLoading] = useState(false);
  const [playlistsLoadingProgress, setPlaylistsLoadingProgress] = useState(0);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuButtonRef = useRef(null);

  // ===== CONTENT TYPE FILTER =====
  const [contentType, setContentType] = useState('LIVE'); // 'LIVE' | 'FILM' | 'ALL'

  // ===== SEARCH STATE =====
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // ===== SERIES GROUPING STATE =====
  const [enableSeriesGrouping, setEnableSeriesGrouping] = useState(true);

  // Set initial default group on first load and when playlist changes
  React.useEffect(() => {
    if (defaultGroup && (!group || group === '')) {
      console.log('✅ Impostando gruppo predefinito:', defaultGroup);
      setGroup(defaultGroup);
    }
  }, [defaultGroup, group]);

  // Auto-load selected playlists after successful authentication
  React.useEffect(() => {
    if (isAuthenticated && isInitialized && user) {
      console.log('🎵 Utente autenticato, avvio caricamento automatico playlist selezionate...');
      console.log('👤 Utente:', user.username);
      handlePlaylistsChange();
    }
  }, [isAuthenticated, isInitialized, user]);

  // Calcola i canali filtrati per tipo di contenuto
  const filteredChannels = useMemo(() => {
    if (contentType === 'ALL') return channels;
    
    return channels.filter(channel => {
      const channelType = detectContentType(channel);
      return channelType === contentType;
    });
  }, [channels, contentType]);

  // Calcola i gruppi unici con conteggio e ordinamento alfabetico
  const groups = useMemo(() => {
    const groupMap = new Map();
    
    // Conta i canali per ogni gruppo (usando canali filtrati)
    filteredChannels.forEach(channel => {
      const groupName = channel.group;
      if (groupName) {
        groupMap.set(groupName, (groupMap.get(groupName) || 0) + 1);
      }
    });
    
    // Converti in array e ordina alfabeticamente
    const groupsWithCount = Array.from(groupMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
    
    return groupsWithCount;
  }, [filteredChannels]);

  const listChannels = useMemo(() => {
    if (group === 'favorites') {
      return contentType === 'ALL' 
        ? favorites 
        : favorites.filter(ch => detectContentType(ch) === contentType);
    }
    return filteredChannels.filter(ch => !group || ch.group === group);
  }, [group, favorites, filteredChannels, contentType]);

  // Remove automatic group selection effect to preserve user choice

  console.log('🔧 App Debug:', {
    totalChannels: channels.length,
    filteredChannels: filteredChannels.length,
    contentType: contentType,
    totalGroups: groups.length,
    currentGroup: group,
    defaultGroup: defaultGroup,
    searchTerm: searchTerm,
    searchResultsCount: searchResults.length
  });

  // Debug content type distribution
  React.useEffect(() => {
    if (channels.length > 0) {
      const liveCount = channels.filter(ch => detectContentType(ch) === 'LIVE').length;
      const filmCount = channels.filter(ch => detectContentType(ch) === 'FILM').length;
      
      console.log('📊 Content Type Distribution:', {
        totalChannels: channels.length,
        liveTV: liveCount,
        films: filmCount,
        currentFilter: contentType,
        filteredCount: filteredChannels.length
      });
    }
  }, [channels, contentType, filteredChannels]);

  // ===== GLOBAL SEARCH LOGIC =====
  
  // Search function - activates only after 3+ chars
  const performSearch = useCallback((term) => {
    const trimmedTerm = term.trim();
    
    if (trimmedTerm.length < 3) {
      // Don't search, but don't show any message
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    
    setTimeout(() => {
      const results = filteredChannels.filter(channel => 
        channel.name.toLowerCase().includes(trimmedTerm.toLowerCase()) ||
        channel.group.toLowerCase().includes(trimmedTerm.toLowerCase()) ||
        (channel.url && channel.url.toLowerCase().includes(trimmedTerm.toLowerCase()))
      );
      
      setSearchResults(results);
      setIsSearching(false);
      console.log(`🔍 Search for "${trimmedTerm}": ${results.length} results`);
    }, 100);
  }, [filteredChannels]);

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce(performSearch, 300),
    [performSearch]
  );

  // Handle search term changes
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    debouncedSearch(term);
  };

  // Reset search when group is selected
  const handleGroupSelect = (groupName) => {
    setSearchTerm('');
    setSearchResults([]);
    setGroup(groupName);
  };

  // Handler per caricamento file M3U
  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    
    reader.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        console.log(`📁 Caricamento file: ${progress}%`);
      }
    };
    
    reader.onload = (event) => {
      parseM3U(event.target.result);
    };
    
    reader.readAsText(file);
  }

  // Handler per gestione delle playlist
  const handlePlaylistsChange = async () => {
    // Avvia il caricamento delle playlist con barra di progresso
    try {
      setPlaylistsLoading(true);
      setPlaylistsLoadingProgress(0);
      
      // Simula progresso del caricamento
      const progressInterval = setInterval(() => {
        setPlaylistsLoadingProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);
      
      // Ricarica le playlist gestite quando cambiano
      await loadManagedPlaylistsContent();
      
      // Completa il progresso
      clearInterval(progressInterval);
      setPlaylistsLoadingProgress(100);
      
      // Aspetta un momento per mostrare il completamento
      setTimeout(() => {
        setPlaylistsLoading(false);
        setPlaylistsLoadingProgress(0);
      }, 500);
    } catch (error) {
      console.error('Errore nel caricamento delle playlist:', error);
      setPlaylistsLoading(false);
      setPlaylistsLoadingProgress(0);
    }
  };

  React.useEffect(() => {
    if (dark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [dark]);

  // Funzione per chiudere il player
  function closePlayer() {
    setSelectedChannel(null);
  }

  // Funzione per selezionare un canale con auto-scroll al player
  function selectChannel(channel) {
    setSelectedChannel(channel);
    // Scroll automatico al videoplayer (con un delay per assicurarsi che sia renderizzato)
    setTimeout(() => {
      // Prima prova a trovare il videoplayer con ID specifico
      const videoPlayer = document.getElementById('video-player-container');
      if (videoPlayer) {
        videoPlayer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        console.log('🎯 Focus automatico sul videoplayer attivato (ID specifico)');
      } else {
        // Fallback al container del videoplayer
        const videoPlayerContainer = document.querySelector('main .bg-black.border-b.border-gray-800');
        if (videoPlayerContainer) {
          videoPlayerContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
          console.log('🎯 Focus automatico sul videoplayer attivato (container)');
        } else {
          // Ultimo fallback al main se il videoplayer non è ancora renderizzato
          const main = document.querySelector('main');
          if (main) {
            main.scrollIntoView({ behavior: 'smooth', block: 'start' });
            console.log('🎯 Fallback focus su main container');
          }
        }
      }
    }, 150); // Delay per il rendering
  }

  // ===== LOADING STATISTICS =====
  
  // Calculate loading statistics for display
  const loadingStats = useMemo(() => {
    if (channels.length === 0) return null;
    
    const liveCount = channels.filter(ch => detectContentType(ch) === 'LIVE').length;
    const filmCount = channels.filter(ch => detectContentType(ch) === 'FILM').length;
    const totalGroups = groups.length;
    
    return {
      total: channels.length,
      live: liveCount,
      films: filmCount,
      groups: totalGroups
    };
  }, [channels, groups]);

  // ===== GROUP MANAGEMENT LOGIC =====

  // Espone i metodi globali per apertura PlaylistManager e toggle tema
  React.useEffect(() => {
    window.setShowPlaylistManager = setShowPlaylistManager;
    window.setShowUserManagement = setShowUserManagement;
    window.setDark = setDark;
    window.openBroadcast = (ch) => selectChannel(ch);
    // Per fallback event-based
    window.addEventListener('openPlaylistManager', () => setShowPlaylistManager(true));
    window.addEventListener('openUserManagement', () => setShowUserManagement(true));
    window.addEventListener('toggleDarkMode', () => setDark((d) => !d));
    window.addEventListener('openBroadcast', (e) => selectChannel(e.detail));
    window.addEventListener('updateChannel', (e) => setSelectedChannel(e.detail));
    return () => {
      delete window.setShowPlaylistManager;
      delete window.setShowUserManagement;
      delete window.setDark;
      delete window.openBroadcast;
      window.removeEventListener('openPlaylistManager', () => setShowPlaylistManager(true));
      window.removeEventListener('openUserManagement', () => setShowUserManagement(true));
      window.removeEventListener('toggleDarkMode', () => setDark((d) => !d));
      window.removeEventListener('openBroadcast', (e) => selectChannel(e.detail));
      window.removeEventListener('updateChannel', (e) => setSelectedChannel(e.detail));
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-purple-900/30 to-black text-white">
      {/* Header Full Width */}
      <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-md border-b border-gray-800/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Left section */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-300 hover:text-white p-1.5 transition-colors duration-200"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              </button>
              <div className="flex items-center gap-3">
                <img
                  src={typeof window !== 'undefined' && window.ktvEnv && window.ktvEnv.isElectron
                    ? new URL('../public/kainolab_logo.png', import.meta.url).href
                    : '/kainolab_logo.png'}
                  alt="KainoTV Logo"
                  className="w-8 h-8 object-contain rounded-lg hidden sm:block"
                />
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent hidden sm:block">
                  kainoTV
                </h1>
              </div>
            </div>

            {/* Search Bar */}
            <div className="flex-1 max-w-md sm:mx-4 mx-2">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Cerca canali..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-800/50 border border-purple-500 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400/50 focus:border-purple-400 transition-all duration-200"
                />
              </div>
            </div>

            {/* Right section */}
            <div className="flex items-center gap-3">
              
              {/* Icona notifiche broadcast */}
              <BroadcastNotificationIcon />

              {/* User Icon Lucide-react, stile hamburger, senza cerchio */}
              <button
                ref={userMenuButtonRef}
                onClick={() => setShowUserMenu((v) => !v)}
                className="text-gray-300 hover:text-white p-1.5 transition-colors duration-200"
                title="Account"
              >
                <User size={24} strokeWidth={2.2} />
              </button>
              {/* UserMenu dropdown senza header/avatar */}
              {showUserMenu && (
                <UserMenu dark={dark} hideHeader buttonRef={userMenuButtonRef} onClose={() => setShowUserMenu(false)} />
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content Area with Sidebar */}
      <div className="flex h-screen overflow-hidden">
        {/* Sidebar */}
        <Sidebar
          groups={groups}
          selectedGroup={group}
          onSelectGroup={handleGroupSelect}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          contentType={contentType}
          onContentTypeChange={setContentType}
          user={user}
        />
        
        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <main className="p-6">
            {/* Video Player */}
            {selectedChannel && (
              <VideoPlayer channel={selectedChannel} onClose={closePlayer} dark={dark} />
            )}
            
            {/* Conditional Content Based on Search Mode */}
            {playlistsLoading ? (
              /* Playlist Loading Screen */
              <div className="text-center py-16 text-gray-400">
                <div className="flex flex-col items-center gap-4">
                  <div className="animate-spin h-8 w-8 border-3 border-purple-400 border-t-transparent rounded-full"></div>
                  <div className="text-lg font-medium">Caricamento Playlist in corso...</div>
                  <div className="w-64 h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-400 to-pink-400 rounded-full transition-all duration-300"
                      style={{ width: `${playlistsLoadingProgress}%` }}
                    />
                  </div>
                  <p className="text-sm opacity-75">{playlistsLoadingProgress}% completato</p>
                </div>
              </div>
            ) : channels.length === 0 ? (
              /* Empty State */
              <div className="flex items-center justify-center min-h-96">
                <div className="text-center max-w-md mx-auto p-8">
                  <div className="flex justify-center mb-6">
                    <img 
                      src={typeof window !== 'undefined' && window.ktvEnv && window.ktvEnv.isElectron
                        ? new URL('../public/kainolab_logo.png', import.meta.url).href
                        : '/kainolab_logo.png'} 
                      alt="KainoTV Logo" 
                      className="w-20 h-20 object-contain rounded-lg shadow-lg" 
                    />
                  </div>
                  <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                    Benvenuto in KainoTV
                  </h2>
                  <p className="text-gray-400 mb-6">
                    Carica una playlist M3U dal menu Gestisci Playlist per iniziare a guardare i tuoi canali preferiti
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={() => setShowPlaylistManager(true)}
                      className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
                    >
                      ⚙️ Gestisci Playlist
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Channel List - Normal or Search results */
              <ChannelList
                channels={searchTerm.length >= 3 ? searchResults : listChannels}
                totalChannels={group === 'favorites' ? favorites.length : channels.length}
                group={searchTerm.length >= 3 ? '' : (group === 'favorites' ? '' : group)}
                onSelect={selectChannel}
                dark={dark}
                isSearchActive={searchTerm.length >= 3}
                searchTerm={searchTerm.length >= 3 ? searchTerm : ''}
                enableSeriesGrouping={enableSeriesGrouping}
                isLoading={isLoading}
                loadingProgress={loadingProgress}
                loadingStats={loadingStats}
                toggleFavorite={toggleFavorite}
                isFavorite={isFavorite}
              />
            )}
          </main>
        </div>
      </div>

      {/* Playlist Manager Modal */}
      {showPlaylistManager && (
        <PlaylistManager
          dark={dark}
          onClose={() => {
            setShowPlaylistManager(false);
            // handlePlaylistsChange(); // RIMOSSO: non ricaricare playlist alla chiusura
          }}
          onPlaylistsChange={handlePlaylistsChange}
        />
      )}

      {showUserManagement && (
        <UserManagement
          dark={dark}
          onClose={() => setShowUserManagement(false)}
        />
      )}

      {/* Sports Chat Widget */}
      <ChatWidget />
    </div>
  );
}

export default AppContent;
